import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useCreateSurvey, useUpdateSurvey, type Survey } from "./api";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";

interface Props {
  open: boolean;
  existing: Survey | null;
  onClose: () => void;
  onCreated?: (survey: Survey) => void;
}

export function SurveyDetailsDialog({ open, existing, onClose, onCreated }: Props) {
  const notify = useNotify();
  const create = useCreateSurvey();
  const update = useUpdateSurvey();
  const [surveyName, setSurveyName] = useState(existing?.surveyName ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{existing ? "Edit survey" : "New survey"}</DialogTitle>
      <DialogContent>
        <TextField
          label="Survey name"
          value={surveyName}
          onChange={(event) => setSurveyName(event.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          fullWidth
          multiline
          minRows={3}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !surveyName.trim()}
          onClick={async () => {
            const payload = { surveyName: surveyName.trim(), description };
            try {
              if (existing) {
                await update.mutateAsync({ surveyId: existing.id, payload });
                notify.success("Survey details updated");
              } else {
                const survey = await create.mutateAsync(payload);
                notify.success("Survey created");
                onCreated?.(survey);
              }
              onClose();
            } catch (error) {
              notify.error(errorMessageFor(error) ?? "Could not save the survey.");
            }
          }}
        >
          {existing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
